import React, { useState, useEffect } from 'react';
import {
  Brain,
  Code,
  Zap,
  Settings,
  Play,
  TestTube,
  Rocket,
  Database,
  MessageSquare,
  BarChart3,
  Shield,
  Users,
  Clock,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Bot,
  Wand2,
  Network,
  Eye,
  Palette,
  Activity,
  Star,
  TrendingUp,
  Globe,
  Cpu,
  Target,
  Lightbulb,
  MoreHorizontal,
  Plus
} from 'lucide-react';

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
}

const AgentStudioBody = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeDemo, setActiveDemo] = useState(0);
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDemo((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const startBuilding = () => {
    setIsBuilding(true);
    setBuildProgress(0);
    const interval = setInterval(() => {
      setBuildProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => setIsBuilding(false), 1000);
          return 100;
        }
        return prev + 2;
      });
    }, 100);
  };

  const demoSteps = [
    {
      title: "Design Agent",
      description: "Configure behavior, personality, and goals",
      icon: <Palette className="w-6 h-6" />,
      color: "from-purple-500 to-pink-500",
      details: ["Visual prompt builder", "Personality templates", "Behavior rules", "Goal definition"]
    },
    {
      title: "Connect Intelligence",
      description: "Link multiple LLMs and knowledge sources",
      icon: <Brain className="w-6 h-6" />,
      color: "from-blue-500 to-cyan-500",
      details: ["Multi-LLM orchestration", "RAG integration", "Vector databases", "Live data streams"]
    },
    {
      title: "Test & Refine",
      description: "Interactive playground with real-time feedback",
      icon: <TestTube className="w-6 h-6" />,
      color: "from-green-500 to-emerald-500",
      details: ["Live conversation testing", "Performance analytics", "A/B testing", "Debug console"]
    },
    {
      title: "Deploy Instantly",
      description: "Production-ready deployment in seconds",
      icon: <Rocket className="w-6 h-6" />,
      color: "from-orange-500 to-red-500",
      details: ["One-click deployment", "Auto-scaling", "Monitoring", "Analytics dashboard"]
    }
  ];

  const features = [
    {
      icon: <Wand2 className="w-8 h-8" />,
      title: "No-Code Visual Builder",
      description: "Drag, drop, and configure agents with our intuitive interface",
      metrics: "10x faster development",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: "Multi-LLM Orchestration",
      description: "Seamlessly combine GPT-4, Claude, Gemini, and more",
      metrics: "15+ LLM integrations",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Smart Knowledge Hub",
      description: "RAG-powered knowledge integration with vector search",
      metrics: "99.9% accuracy",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-time Execution",
      description: "Sub-second response times with intelligent caching",
      metrics: "<200ms latency",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Enterprise Security",
      description: "SOC 2 compliant with advanced access controls",
      metrics: "Zero breaches",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Advanced Analytics",
      description: "Deep insights into agent performance and usage",
      metrics: "360° visibility",
      color: "from-indigo-500 to-purple-500"
    }
  ];

  const agentShowcase = [
    {
      name: "Customer Support Pro",
      type: "Support Agent",
      status: "Live",
      requests: "12.5K",
      satisfaction: "98%",
      avatar: "🤖"
    },
    {
      name: "Document Analyzer",
      type: "Data Agent",
      status: "Processing",
      requests: "3.2K",
      satisfaction: "96%",
      avatar: "📄"
    },
    {
      name: "Sales Assistant",
      type: "Sales Agent",
      status: "Live",
      requests: "8.7K",
      satisfaction: "94%",
      avatar: "💼"
    }
  ];

  const platformStats = [
    { number: "1000+", label: "Agents Built", icon: <Bot className="w-6 h-6" /> },
    { number: "99.9%", label: "Uptime", icon: <Activity className="w-6 h-6" /> },
    { number: "<1min", label: "Deploy Time", icon: <Rocket className="w-6 h-6" /> },
    { number: "15+", label: "LLM Configurations", icon: <Brain className="w-6 h-6" /> }
  ];

  return (
    <>
      <main className="bg-black text-white font-sans overflow-hidden relative">
        {/* Dynamic Background */}
        <div className="fixed inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-30 transition-all duration-1000"
            style={{
              background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(139, 92, 246, 0.15), transparent 40%)`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-blue-900/20" />
          
          {/* Animated Orbs */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-16 h-16 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-full blur-xl animate-bounce" />
          <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full blur-lg animate-ping" />
        </div>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            {/* Floating Badge */}
            <br/><br/><br/>
            
            {/* Main Title */}
            <h1 className="text-6xl md:text-8xl font-black mb-8 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
              Agent Studio
              <span className="block mt-4 text-4xl md:text-6xl bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                Where AI Comes Alive
              </span>
            </h1>
            
            {/* Subtitle */}
            <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
              <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
                Build, orchestrate, and deploy intelligent AI agents that transform how work gets done. 
                No code required. Infinite possibilities unlocked.
              </p>
            </div>

            {/* Interactive CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16 opacity-0 animate-fade-in-up" style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
              <button 
                onClick={startBuilding}
                className="group relative bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-2xl shadow-purple-500/25 hover:shadow-purple-500/50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
                <div className="relative flex items-center space-x-3">
                  <Wand2 className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
                  <span>Start Building Now</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </button>
              
              <button className="group border-2 border-white/20 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-xl">
                <div className="flex items-center space-x-3">
                  <Play className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                  <span>Watch Demo</span>
                </div>
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto opacity-0 animate-fade-in-up" style={{ animationDelay: '1.5s', animationFillMode: 'forwards' }}>
              {platformStats.map((stat, index) => (
                <div 
                  key={index} 
                  className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 hover:scale-105"
                  style={{ animationDelay: `${2 + index * 0.1}s` }}
                >
                  <div className="text-purple-400 mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors duration-300">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Demo Section */}
        <section className="relative py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Experience the Magic
              </h2>
              <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
                Watch AI agents come to life through our revolutionary visual builder
              </p>
            </div>

            {/* Enhanced Interactive Builder Demo */}
            <div className="relative bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-2xl border border-purple-500/30 rounded-3xl p-8 mb-16 overflow-hidden">
              {/* Animated Connection Lines */}
              <div className="absolute inset-0 pointer-events-none">
                <svg className="w-full h-full" viewBox="0 0 1200 600">
                  <defs>
                    <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(139, 92, 246, 0)" />
                      <stop offset="50%" stopColor="rgba(139, 92, 246, 0.6)" />
                      <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
                    </linearGradient>
                  </defs>
                  {activeDemo !== null && (
                    <>
                      <path 
                        d="M 300 150 Q 600 100 900 150" 
                        stroke="url(#connectionGradient)" 
                        strokeWidth="2" 
                        fill="none"
                        className="animate-pulse"
                        style={{ 
                          strokeDasharray: '10 5',
                          animation: 'dash 2s linear infinite'
                        }}
                      />
                      <path 
                        d="M 300 400 Q 600 450 900 400" 
                        stroke="url(#connectionGradient)" 
                        strokeWidth="2" 
                        fill="none"
                        className="animate-pulse"
                        style={{ 
                          strokeDasharray: '10 5',
                          animation: 'dash 2s linear infinite',
                          animationDelay: '0.5s'
                        }}
                      />
                    </>
                  )}
                </svg>
              </div>

              {/* Enhanced Header */}
              <div className="relative flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse hover:scale-125 transition-transform cursor-pointer" />
                    <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse hover:scale-125 transition-transform cursor-pointer" style={{ animationDelay: '0.2s' }} />
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse hover:scale-125 transition-transform cursor-pointer" style={{ animationDelay: '0.4s' }} />
                  </div>
                  <h3 className="text-xl font-bold text-white bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                    Agent Studio v2.0
                  </h3>
                  <div className="px-3 py-1 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-full">
                    <span className="text-xs text-purple-300 font-medium">BETA</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-ping" />
                    <span>Live Demo</span>
                  </div>
                  <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                    <MoreHorizontal className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Enhanced Builder Panel */}
                <div className="relative bg-black/50 rounded-2xl p-6 border border-white/10 hover:border-purple-500/30 transition-all duration-500 group">
                  {/* Panel Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                  
                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="font-bold text-white flex items-center space-x-2">
                        <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
                          <Settings className="w-4 h-4 text-white" />
                        </div>
                        <span>Agent Builder</span>
                      </h4>
                      <div className="flex space-x-1">
                        <div className="w-1 h-4 bg-purple-400/60 rounded-full animate-pulse" />
                        <div className="w-1 h-4 bg-blue-400/40 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                        <div className="w-1 h-4 bg-cyan-400/20 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {demoSteps.map((step, index) => (
                        <div 
                          key={index}
                          className={`relative p-4 rounded-xl border transition-all duration-700 cursor-pointer overflow-hidden group/step ${
                            activeDemo === index 
                              ? 'border-purple-500 bg-gradient-to-r from-purple-500/20 to-blue-500/20 scale-105 shadow-lg shadow-purple-500/25' 
                              : 'border-white/10 hover:border-white/30 hover:scale-[1.02]'
                          }`}
                          onClick={() => setActiveDemo(index)}
                        >
                          {/* Selection Ripple Effect */}
                          {activeDemo === index && (
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-blue-500/30 rounded-xl animate-pulse" />
                          )}
                          
                          {/* Connection Indicator */}
                          <div className={`absolute right-2 top-2 w-2 h-2 rounded-full transition-all duration-500 ${
                            activeDemo === index 
                              ? 'bg-green-400 animate-ping' 
                              : 'bg-gray-600'
                          }`} />
                          
                          <div className="relative">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className={`p-2 rounded-lg bg-gradient-to-r ${step.color} transition-all duration-500 ${
                                activeDemo === index ? 'scale-110 rotate-3' : ''
                              }`}>
                                {step.icon}
                              </div>
                              <div className="flex-1">
                                <div className={`font-semibold text-sm transition-colors duration-300 ${
                                  activeDemo === index ? 'text-white' : 'text-gray-300'
                                }`}>
                                  {step.title}
                                </div>
                                <div className="text-xs text-gray-400">{step.description}</div>
                              </div>
                              {activeDemo === index && (
                                <CheckCircle className="w-4 h-4 text-green-400 animate-bounce" />
                              )}
                            </div>
                            
                            {/* Expandable Details with Smooth Animation */}
                            <div className={`overflow-hidden transition-all duration-700 ${
                              activeDemo === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="mt-4 pt-3 border-t border-white/10">
                                <div className="grid grid-cols-1 gap-2">
                                  {step.details.map((detail, i) => (
                                    <div 
                                      key={i} 
                                      className="flex items-center space-x-2 animate-fade-in"
                                      style={{ animationDelay: `${i * 0.1}s` }}
                                    >
                                      <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                                      <span className="text-xs text-gray-300">{detail}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Progress Indicator */}
                    <div className="mt-6 pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                        <span>Configuration Progress</span>
                        <span>{Math.round(((activeDemo + 1) / demoSteps.length) * 100)}%</span>
                      </div>
                      <div className="w-full bg-black/30 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-400 to-blue-400 h-2 rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${((activeDemo + 1) / demoSteps.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Live Preview Panel */}
                <div className="relative bg-black/50 rounded-2xl p-6 border border-white/10 hover:border-blue-500/30 transition-all duration-500 group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                  
                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="font-bold text-white flex items-center space-x-2">
                        <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg">
                          <Eye className="w-4 h-4 text-white" />
                        </div>
                        <span>Live Preview</span>
                      </h4>
                      <div className="flex items-center space-x-2">
                        <div className={`px-2 py-1 rounded-full text-xs font-medium transition-all duration-500 ${
                          activeDemo === 3 ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {activeDemo === 3 ? 'Deployed' : 'Testing'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Dynamic Agent Preview */}
                      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-4 transition-all duration-700 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className={`w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center transition-all duration-500 ${
                            activeDemo === 0 ? 'animate-pulse scale-110' : ''
                          }`}>
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-white text-sm transition-all duration-500">
                              {activeDemo === 0 ? 'Configuring Agent...' : 
                               activeDemo === 1 ? 'Customer Support Agent' :
                               activeDemo === 2 ? 'Testing Agent Responses...' :
                               'Production Ready Agent'}
                            </div>
                            <div className="text-xs text-gray-400">
                              {activeDemo === 0 ? 'Setting up behavior' :
                               activeDemo === 1 ? 'Connected to knowledge base' :
                               activeDemo === 2 ? 'Running validation tests' :
                               'Live and responding'}
                            </div>
                          </div>
                          <div className="ml-auto">
                            <div className={`w-2 h-2 rounded-full transition-all duration-500 ${
                              activeDemo === 3 ? 'bg-green-400 animate-ping' : 
                              activeDemo === 2 ? 'bg-yellow-400 animate-pulse' :
                              'bg-blue-400 animate-pulse'
                            }`} />
                          </div>
                        </div>
                        
                        {/* Dynamic Response Preview */}
                        <div className="bg-black/40 rounded-lg p-3 mb-3 transition-all duration-700">
                          <div className="text-xs text-gray-400 mb-1">
                            {activeDemo === 0 ? 'Configuration:' :
                             activeDemo === 1 ? 'Sample Response:' :
                             activeDemo === 2 ? 'Test Result:' :
                             'Latest Response:'}
                          </div>
                          <div className="text-sm text-white transition-all duration-700">
                            {activeDemo === 0 ? '"Setting up personality and behavior rules..."' :
                             activeDemo === 1 ? '"I\'d be happy to help you with your order status. Let me check that for you..."' :
                             activeDemo === 2 ? '"Test passed: Response accuracy 98.5%, Latency 0.2s"' :
                             '"Your order #12345 is currently being processed and will ship tomorrow."'}
                          </div>
                        </div>
                        
                        {/* Dynamic Metrics */}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-gray-400">
                            Response: 
                            <span className={`text-white font-medium ml-1 transition-all duration-500 ${
                              activeDemo === 2 ? 'text-yellow-400' : activeDemo === 3 ? 'text-green-400' : ''
                            }`}>
                              {activeDemo === 0 ? '---' :
                               activeDemo === 1 ? '0.8s' :
                               activeDemo === 2 ? '0.3s' :
                               '0.2s'}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            Accuracy: 
                            <span className={`text-white font-medium ml-1 transition-all duration-500 ${
                              activeDemo >= 2 ? 'text-green-400' : ''
                            }`}>
                              {activeDemo === 0 ? '---' :
                               activeDemo === 1 ? '95%' :
                               activeDemo === 2 ? '98%' :
                               '99%'}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            Status: 
                            <span className={`font-medium ml-1 transition-all duration-500 ${
                              activeDemo === 0 ? 'text-blue-400' :
                              activeDemo === 1 ? 'text-purple-400' :
                              activeDemo === 2 ? 'text-yellow-400' :
                              'text-green-400'
                            }`}>
                              {activeDemo === 0 ? 'Setup' :
                               activeDemo === 1 ? 'Ready' :
                               activeDemo === 2 ? 'Testing' :
                               'Live'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Code Generation Preview */}
                      {activeDemo >= 1 && (
                        <div className="bg-black/60 rounded-xl p-4 border border-gray-700 transition-all duration-700 animate-fade-in">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Code className="w-4 h-4 text-green-400" />
                              <span className="text-sm font-medium text-white">Generated Code</span>
                            </div>
                            <div className="text-xs text-gray-400">Python</div>
                          </div>
                          <div className="bg-black/60 rounded p-3 font-mono text-xs overflow-hidden">
                            <div className="text-green-400"># Auto-generated agent code</div>
                            <div className="text-blue-400">class CustomerSupportAgent:</div>
                            <div className="text-gray-300 ml-4">def __init__(self):</div>
                            <div className="text-gray-300 ml-8">self.llm = GPT4()</div>
                            {activeDemo >= 2 && (
                              <div className="text-gray-300 ml-8 animate-fade-in">self.knowledge_base = VectorDB()</div>
                            )}
                            {activeDemo >= 3 && (
                              <div className="text-yellow-400 ml-4 animate-fade-in">def deploy(self): ...</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Building Progress */}
                      {isBuilding && (
                        <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 animate-pulse">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="animate-spin">
                              <Settings className="w-5 h-5 text-yellow-400" />
                            </div>
                            <span className="text-yellow-300 font-medium">Building Agent...</span>
                          </div>
                          <div className="w-full bg-black/30 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-yellow-400 to-orange-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${buildProgress}%` }}
                            />
                          </div>
                          <div className="text-xs text-yellow-400 mt-2">{buildProgress}% Complete</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Enhanced Agent Gallery */}
                <div className="relative bg-black/50 rounded-2xl p-6 border border-white/10 hover:border-green-500/30 transition-all duration-500 group">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                  
                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="font-bold text-white flex items-center space-x-2">
                        <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg">
                          <Users className="w-4 h-4 text-white" />
                        </div>
                        <span>Live Agents</span>
                      </h4>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-ping" />
                        <span className="text-xs text-green-400 font-medium">3 Active</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {agentShowcase.map((agent, index) => (
                        <div 
                          key={index} 
                          className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-4 hover:scale-105 transition-all duration-500 hover:shadow-lg hover:shadow-green-500/20 cursor-pointer group/agent"
                          onClick={() => {
                            // Add some interaction feedback
                            const element = document.getElementById(`agent-${index}`);
                            if (element) {
                              element.style.transform = 'scale(0.95)';
                              setTimeout(() => {
                                element.style.transform = 'scale(1.05)';
                              }, 100);
                            }
                          }}
                        >
                          <div id={`agent-${index}`} className="transition-transform duration-200">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="text-2xl group-hover/agent:animate-bounce transition-all duration-300">
                                {agent.avatar}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-white text-sm group-hover/agent:text-green-300 transition-colors">
                                  {agent.name}
                                </div>
                                <div className="text-xs text-gray-400">{agent.type}</div>
                              </div>
                              <div className={`px-2 py-1 rounded-full text-xs font-medium transition-all duration-300 ${
                                agent.status === 'Live' 
                                  ? 'bg-green-500/20 text-green-400 group-hover/agent:bg-green-500/30' 
                                  : 'bg-yellow-500/20 text-yellow-400 group-hover/agent:bg-yellow-500/30'
                              }`}>
                                {agent.status}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="text-gray-400">
                                Requests: 
                                <span className="text-white font-medium ml-1 group-hover/agent:text-green-300 transition-colors">
                                  {agent.requests}
                                </span>
                              </div>
                              <div className="text-gray-400">
                                Rating: 
                                <span className="text-green-400 font-medium ml-1 group-hover/agent:text-green-300 transition-colors">
                                  {agent.satisfaction}
                                </span>
                              </div>
                            </div>

                            {/* Activity Graph */}
                            <div className="mt-3 pt-3 border-t border-gray-700">
                              <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                                <span>Activity (24h)</span>
                                <TrendingUp className="w-3 h-3" />
                              </div>
                              <div className="flex space-x-1 h-8">
                                {Array.from({ length: 12 }, (_, i) => (
                                  <div
                                    key={i}
                                    className="flex-1 bg-gradient-to-t from-green-600/20 to-green-400/40 rounded-sm transition-all duration-300 hover:from-green-500/30 hover:to-green-300/50"
                                    style={{ 
                                      height: `${Math.random() * 80 + 20}%`,
                                      animationDelay: `${i * 0.1}s`
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Agent Button */}
                    <button className="w-full mt-4 border-2 border-dashed border-gray-600 rounded-xl p-4 text-gray-400 hover:border-green-500 hover:text-green-400 transition-all duration-300 group/add">
                      <div className="flex items-center justify-center space-x-2">
                        <Plus className="w-4 h-4 group-hover/add:rotate-90 transition-transform duration-300" />
                        <span className="text-sm font-medium">Add New Agent</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Bottom Status Bar */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6 text-sm text-gray-400">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-green-400" />
                      <span>System Status: Operational</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span>Last Update: 2 min ago</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:scale-105 transition-transform duration-300 text-sm font-medium">
                      Try Full Version
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Showcase */}
        <section className="relative py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Superpowers Included
              </h2>
              <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
                Everything you need to build enterprise-grade AI agents
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:border-purple-500/50 transition-all duration-500 cursor-pointer ${
                    activeFeature === index ? 'scale-105 border-purple-500/70 shadow-2xl shadow-purple-500/20' : ''
                  }`}
                  onMouseEnter={() => setActiveFeature(index)}
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative">
                    <div className={`text-transparent bg-gradient-to-r ${feature.color} bg-clip-text mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {feature.icon}
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    
                    <p className="text-gray-300 mb-6 leading-relaxed">
                      {feature.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className={`px-4 py-2 rounded-full bg-gradient-to-r ${feature.color} bg-opacity-20 border border-current border-opacity-20`}>
                        <span className="text-sm font-semibold text-white">{feature.metrics}</span>
                      </div>
                      
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-400 group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="relative py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <div className="relative bg-gradient-to-br from-purple-600/30 to-blue-600/30 backdrop-blur-2xl border border-purple-500/50 rounded-3xl p-16 overflow-hidden">
              {/* Animated Background Elements */}
              <div className="absolute top-0 left-0 w-full h-full opacity-30">
                <div className="absolute top-10 left-10 w-20 h-20 bg-purple-500/20 rounded-full blur-xl animate-pulse" />
                <div className="absolute bottom-10 right-10 w-16 h-16 bg-blue-500/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-pink-500/10 rounded-full blur-2xl animate-spin" style={{ animationDuration: '20s' }} />
              </div>
              
              <div className="relative">
                <div className="inline-block p-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mb-8 animate-bounce">
                  <Rocket className="w-12 h-12 text-white" />
                </div>
                
                <h2 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                  Ready to Build the Future?
                </h2>
                
                <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
                  Join thousands of innovators using Agent Studio to create AI agents that transform businesses. 
                  Start building your first agent in under 60 seconds.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <button className="group relative bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-2xl shadow-purple-500/25">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
                    <div className="relative flex items-center space-x-3">
                      <Sparkles className="w-6 h-6 group-hover:rotate-180 transition-transform duration-500" />
                      <span>Start Free Trial</span>
                      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </button>
                  
                  <button className="group border-2 border-white/30 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-xl">
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                      <span>Book Demo</span>
                    </div>
                  </button>
                </div>
                
                <div className="mt-12 text-sm text-gray-400">
                  No credit card required • Setup in 60 seconds • Cancel anytime
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <style >{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes dash {
          from {
            stroke-dashoffset: 20;
          }
          to {
            stroke-dashoffset: 0;
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </>
  );
};

export default AgentStudioBody;